// Physical paint section of the Paint Tool settings: the Paint Lab's controls
// (medium, brush, pigments, loading, the advanced medium multipliers, brush
// rotation, environment) plus impasto appearance and the document surface.

import { MEDIUM_STOPS } from '../materials/MediumModel.js';
import { BRUSH_PRESETS_BY_ID } from '../brushes/BrushPresets.js';
import { PIGMENTS_BY_ID } from '../pigments/PigmentLibrary.js';
import { normaliseSurfaceSettings, DEFAULT_SURFACE_SETTINGS } from '../surfaces/SurfaceLibrary.js';

export const DEFAULT_PHYSICAL_PAINT_SETTINGS = Object.freeze({
  medium: 0.84,
  brush: 'filbert_hog',
  pigment: 'picker', // 'picker' = the Paint Tool colour picker; otherwise a library pigment id
  secondPigment: '',
  mix: 0.5,
  loading: 1,
  // Multipliers on the medium's physical parameters (MediumModel.resolveMedium).
  waterScale: 1,
  thicknessScale: 1,
  pigmentScale: 1,
  fluidityScale: 1,
  dryingScale: 1,
  pickupScale: 1,
  rotationMode: 'direction',
  humidity: 0.4,
  timeScale: 1,
  impastoDepth: 1,
  surface: DEFAULT_SURFACE_SETTINGS,
});

const ROTATION_MODES = ['direction', 'pen', 'fixed'];

export function normalisePhysicalPaintSettings(value, previous = DEFAULT_PHYSICAL_PAINT_SETTINGS) {
  const v = value && typeof value === 'object' ? value : {};
  const p = previous && typeof previous === 'object' ? previous : DEFAULT_PHYSICAL_PAINT_SETTINGS;
  const num = (x, lo, hi, d) => (Number.isFinite(Number(x)) ? Math.max(lo, Math.min(hi, Number(x))) : d);
  const brush = v.brush ?? p.brush;
  const pigment = v.pigment ?? p.pigment;
  const second = v.secondPigment ?? p.secondPigment;
  return {
    medium: num(v.medium, 0, 1, p.medium ?? DEFAULT_PHYSICAL_PAINT_SETTINGS.medium),
    brush: BRUSH_PRESETS_BY_ID[brush] && brush !== 'palette_knife' ? brush : DEFAULT_PHYSICAL_PAINT_SETTINGS.brush,
    pigment: pigment === 'picker' || PIGMENTS_BY_ID[pigment] ? pigment : 'picker',
    secondPigment: PIGMENTS_BY_ID[second] ? second : '',
    mix: num(v.mix, 0, 1, p.mix ?? 0.5),
    loading: num(v.loading, 0, 1, p.loading ?? 1),
    waterScale: num(v.waterScale, 0, 3, p.waterScale ?? 1),
    thicknessScale: num(v.thicknessScale, 0, 3, p.thicknessScale ?? 1),
    pigmentScale: num(v.pigmentScale, 0, 3, p.pigmentScale ?? 1),
    fluidityScale: num(v.fluidityScale, 0.1, 10, p.fluidityScale ?? 1),
    dryingScale: num(v.dryingScale, 0, 5, p.dryingScale ?? 1),
    pickupScale: num(v.pickupScale, 0, 2, p.pickupScale ?? 1),
    rotationMode: ROTATION_MODES.includes(v.rotationMode ?? p.rotationMode) ? (v.rotationMode ?? p.rotationMode) : 'direction',
    humidity: num(v.humidity, 0, 0.95, p.humidity ?? 0.4),
    timeScale: num(v.timeScale, 0, 8, p.timeScale ?? 1),
    impastoDepth: num(v.impastoDepth, 0, 3, p.impastoDepth ?? 1),
    surface: normaliseSurfaceSettings({ ...(p.surface || {}), ...(v.surface || {}) }),
  };
}

export function nearestMediumStop(value) {
  let best = MEDIUM_STOPS[0];
  for (const stop of MEDIUM_STOPS) if (Math.abs(stop.at - value) < Math.abs(best.at - value)) best = stop;
  return best;
}
